import { useEffect, useState, type ReactNode } from 'react';
import {
  socialApi,
  summarizeCommandCenter,
  EMPTY_COMMAND_CENTER,
  type CommandCenterSummary,
} from '@/lib/social-api';
import { Share2, CalendarDays, AlertTriangle, Link2 } from 'lucide-react';

export function SocialInsightsStrip({ onNavigate }: { onNavigate?: (path: string) => void }) {
  const [data, setData] = useState<CommandCenterSummary | null>(null);

  useEffect(() => {
    void socialApi
      .commandCenter()
      .then((raw) => setData(summarizeCommandCenter(raw)))
      .catch(() => setData(EMPTY_COMMAND_CENTER));
  }, []);

  const d = data ?? EMPTY_COMMAND_CENTER;
  const go = (path: string) => {
    if (onNavigate) onNavigate(path);
    else window.location.assign(path);
  };

  return (
    <section className="w-full rounded-lg border border-border bg-background p-4 dark:bg-card/40">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Share2 className="h-4 w-4 text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Social Insights</h2>
        </div>
        <button
          type="button"
          className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
          onClick={() => go('/social')}
        >
          Zum Social Hub
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <InsightStat
          icon={<Link2 className="h-3.5 w-3.5" />}
          label="Aktive Kanäle"
          value={d.accounts_active}
          onClick={() => go('/social/accounts')}
        />
        <InsightStat
          icon={<CalendarDays className="h-3.5 w-3.5" />}
          label="Geplant (7 Tage)"
          value={d.scheduled_next_7_days}
          onClick={() => go('/social/queue')}
        />
        <InsightStat
          icon={<AlertTriangle className="h-3.5 w-3.5" />}
          label="Fehler"
          value={d.failed_targets}
          tone={d.failed_targets > 0 ? 'danger' : 'default'}
          onClick={() => go('/social/queue')}
        />
        <InsightStat
          icon={<AlertTriangle className="h-3.5 w-3.5" />}
          label="Tokens abgelaufen"
          value={d.accounts_expired}
          tone={d.accounts_expired > 0 ? 'warn' : 'default'}
          onClick={() => go('/social/accounts')}
        />
      </div>
    </section>
  );
}

function InsightStat({
  label,
  value,
  icon,
  tone = 'default',
  onClick,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  tone?: 'default' | 'danger' | 'warn';
  onClick?: () => void;
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-danger/30 bg-danger-soft text-danger'
      : tone === 'warn'
        ? 'border-warning/30 bg-warning-soft text-warning'
        : 'border-border bg-card text-foreground dark:bg-background';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition hover:opacity-90 ${toneClass}`}
    >
      <span className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        {icon}
        {label}
      </span>
      <span className="text-lg font-bold tabular-nums">{value}</span>
    </button>
  );
}
